import { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { useTheme } from '../../context/ThemeProvider';

interface MermaidDiagramProps {
  chart: string;
  className?: string;
}

export const MermaidDiagram = ({ chart, className = '' }: MermaidDiagramProps) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const { isDark } = useTheme();

  useEffect(() => {
    if (!elementRef.current) return;

    // Initialize Mermaid with theme-appropriate settings
    mermaid.initialize({
      startOnLoad: false,
      theme: isDark ? 'dark' : 'default',
      securityLevel: 'loose',
      fontFamily: 'var(--cs-font-family-sans)',
      fontSize: 14,
      themeVariables: {
        primaryColor: isDark ? '#3b82f6' : '#2563eb',
        primaryTextColor: isDark ? '#f9fafb' : '#111827',
        primaryBorderColor: isDark ? '#4b5563' : '#d1d5db',
        lineColor: isDark ? '#6b7280' : '#4b5563',
        secondaryColor: isDark ? '#1f2937' : '#f3f4f6',
        tertiaryColor: isDark ? '#374151' : '#e5e7eb',
        background: isDark ? '#111827' : '#ffffff',
        mainBkg: isDark ? '#1f2937' : '#ffffff',
        secondBkg: isDark ? '#374151' : '#f9fafb',
        tertiaryBkg: isDark ? '#4b5563' : '#f3f4f6'
      }
    });

    const renderDiagram = async () => {
      try {
        setError(null);
        
        // Clear previous content
        if (elementRef.current) {
          elementRef.current.innerHTML = '';
        }

        // Generate unique ID for this diagram
        const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
        
        // Render the diagram
        const { svg } = await mermaid.render(id, chart);
        
        if (elementRef.current) {
          elementRef.current.innerHTML = svg;
          
          // Style the SVG for better integration
          const svgElement = elementRef.current.querySelector('svg');
          if (svgElement) {
            svgElement.style.maxWidth = '100%';
            svgElement.style.height = 'auto';
            svgElement.style.display = 'block';
            svgElement.style.margin = '0 auto';
          }
        }
      } catch (err) {
        console.error('Mermaid rendering error:', err);
        setError(err instanceof Error ? err.message : 'Failed to render diagram');
      }
    };

    renderDiagram();
  }, [chart, isDark]);

  if (error) {
    return (
      <div className={`mermaid-error p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg ${className}`}>
        <div className="flex items-start space-x-2">
          <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-200">
              Mermaid Diagram Error
            </p>
            <p className="text-sm text-red-600 dark:text-red-300 mt-1">
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={elementRef} 
      className={`mermaid-diagram p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm ${className}`}
    />
  );
};